import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('Password minimal 8 karakter');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Password tidak cocok');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Password berhasil direset!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <p className="text-slate-600 dark:text-zinc-400">Link reset tidak valid.</p>
          <Link to="/forgot-password">
            <Button>Minta Link Baru</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-8">
          {success ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50">Password Berhasil Direset!</h2>
              <p className="text-sm text-slate-600 dark:text-zinc-400">
                Silakan login dengan password baru Anda.
              </p>
              <Button onClick={() => navigate('/login')} className="bg-indigo-600 hover:bg-indigo-700 mt-4" data-testid="go-to-login-btn">
                Login Sekarang
              </Button>
            </div>
          ) : (
            <>
              <Link to="/login" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-slate-200 mb-6">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Kembali ke Login
              </Link>

              <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mb-2" style={{ fontFamily: 'Manrope' }}>
                Reset Password
              </h1>
              <p className="text-slate-600 dark:text-zinc-400 mb-6 text-sm">
                Masukkan password baru untuk akun Anda.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="password" className="text-slate-700 dark:text-zinc-300 mb-2 block">Password Baru</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimal 8 karakter"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 py-6 rounded-xl dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                      required
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-600 dark:text-zinc-400"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm" className="text-slate-700 dark:text-zinc-300 mb-2 block">Konfirmasi Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                    <Input
                      id="confirm"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Ulangi password baru"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 py-6 rounded-xl dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                      required
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-xl py-6"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : 'Reset Password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
